import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type { BatchDetailDto, BatchDto, BatchPreviewDto, LocationDto, PrinterDto, WarehouseDto } from '@thebb/shared';
import { get, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';
import { PackagesEditor } from './Packaging';
import { ShippingPanel } from '../components/Shipping';

export const batchStatusLabel: Record<string, string> = { created: 'Oprettet', printed: 'Printet', picking: 'Plukkes', picked: 'Plukket', packed: 'Pakket', staged: 'På palleplads', shipped: 'Sendt', cancelled: 'Annulleret' };
export const batchTone = (s: string) => (s === 'shipped' ? 'green' : s === 'cancelled' ? 'red' : ['created', 'printed'].includes(s) ? 'blue' : 'amber') as 'green' | 'red' | 'blue' | 'amber';
const kindLabel: Record<string, string> = { single_shipment: 'Samlet levering', multi_shipment: 'Enkeltforsendelser' };
const docLabel: Record<string, string> = { overview: 'Oversigt', pickList: 'Plukliste', totals: 'Totaler', giftList: 'Gaveliste' };

function useStationPrinter() {
  const [printerId, setPrinterIdState] = useState(() => { try { return localStorage.getItem('station.laserPrinterId') ?? ''; } catch { return ''; } });
  const setPrinterId = (v: string) => { setPrinterIdState(v); try { localStorage.setItem('station.laserPrinterId', v); } catch { /* ignore */ } };
  return { printerId, setPrinterId };
}

/** All batches, newest first; filter by status. */
export function BatchesPage() {
  const [status, setStatus] = useState('');
  const params = useMemo(() => { const p = new URLSearchParams({ perPage: '100' }); if (status) p.set('status', status); return p.toString(); }, [status]);
  const list = useQuery({ queryKey: ['batches', params], queryFn: () => get<{ items: BatchDto[]; total: number }>(`/batches?${params}`), refetchInterval: 30_000 });
  return (
    <div>
      <PageTitle
        title="Pakkebatches"
        subtitle="Gaveshops pakkes i batches efter lukning"
        actions={
          <select className="input w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Alle statusser</option>
            {Object.entries(batchStatusLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        }
      />
      {list.isLoading ? <Spinner /> : <BatchTable items={list.data?.items ?? []} />}
    </div>
  );
}

export function BatchTable({ items }: { items: BatchDto[] }) {
  return (
    <table className="table card p-0">
      <thead><tr><th>Batch</th><th>Shop</th><th>Art</th><th>Status</th><th className="text-right">Ordrer</th><th className="text-right">Stk</th><th>Lager</th><th>Palleplads</th><th>Pakket af</th><th>Oprettet</th></tr></thead>
      <tbody>
        {items.map((b) => (
          <tr key={b.id}>
            <td><Link to="/batches/$id" params={{ id: b.id }} className="font-medium text-primary hover:underline">{b.number}</Link></td>
            <td><Link to="/shops/$id" params={{ id: b.shopId }} className="hover:text-primary">{b.shopName}</Link></td>
            <td>{kindLabel[b.kind]}</td>
            <td><Badge tone={batchTone(b.status)}>{batchStatusLabel[b.status]}</Badge></td>
            <td className="text-right tabular-nums">{b.orderCount}</td>
            <td className="text-right tabular-nums">{b.unitCount}</td>
            <td>{b.warehouseCode}</td>
            <td>{b.stagingLocationCode ?? ''}</td>
            <td>{b.packedBy ?? ''}</td>
            <td className="whitespace-nowrap">{fmtTime(b.createdAt)}</td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={10} className="text-muted">Ingen batches.</td></tr>}
      </tbody>
    </table>
  );
}

/** "Ny pakkebatch": kind, count, warehouse, department; shows what is packable before creating. */
export function NewBatchModal({ shopId, onClose, onCreated, onError }: { shopId: string; onClose: () => void; onCreated: (b: BatchDetailDto) => void; onError: (e: unknown) => void }) {
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  const printers = useQuery({ queryKey: ['printers'], queryFn: () => get<PrinterDto[]>('/printing/printers') });
  const station = useStationPrinter();
  const [form, setForm] = useState({ kind: 'single_shipment', limit: '200', warehouseId: '', department: '', print: true });
  const previewBody = { shopId, kind: form.kind, limit: Number(form.limit) || 200, warehouseId: form.warehouseId || undefined, department: form.department || undefined };
  const preview = useQuery({ queryKey: ['batch-preview', shopId, JSON.stringify(previewBody)], queryFn: () => post<BatchPreviewDto>('/batches/preview', previewBody) });
  const create = useMutation({ mutationFn: () => post<BatchDetailDto>('/batches', { ...previewBody, printerId: form.print && station.printerId ? station.printerId : undefined }), onSuccess: onCreated, onError });
  const p = preview.data;
  return (
    <Modal title="Ny pakkebatch" onClose={onClose}>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="label">Art</label>
          <select className="input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="single_shipment">Samlet levering til én adresse</option>
            <option value="multi_shipment">Enkeltforsendelser (hver ordre sin pakke)</option>
          </select>
        </div>
        <div>
          <label className="label">Antal ordrer (højst)</label>
          <input className="input" type="number" min={1} max={2000} value={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.value })} />
        </div>
        <div>
          <label className="label">Lager</label>
          <select className="input" value={form.warehouseId} onChange={(e) => setForm({ ...form, warehouseId: e.target.value })}>
            <option value="">Efter reglen{p ? ` (${p.warehouseCode})` : ''}</option>
            {warehouses.data?.filter((w) => w.active && w.type === 'operated').map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Afdeling (valgfri)</label>
          <select className="input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
            <option value="">Alle afdelinger</option>
            {p?.departments.filter((d) => d.department).map((d) => <option key={d.department} value={d.department}>{d.department} ({d.orders})</option>)}
          </select>
        </div>
        <div className="md:col-span-2 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.print} onChange={(e) => setForm({ ...form, print: e.target.checked })} /> Print de fire dokumenter på</label>
          <select className="input w-56" value={station.printerId} onChange={(e) => station.setPrinterId(e.target.value)} disabled={!form.print}>
            <option value="">Vælg printer</option>
            {printers.data?.filter((x) => x.enabled && x.kind === 'pdf').map((x) => <option key={x.id} value={x.id}>{x.displayName}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-4 rounded-xl bg-canvas p-3 text-sm">
        {preview.isLoading || !p ? (
          <span className="text-muted">Beregner…</span>
        ) : (
          <>
            <div><b>{p.packable}</b> ordrer kan pakkes nu fra {p.warehouseCode} ({p.units} stk) · <b>{p.unpackable}</b> kan ikke pakkes fuldt ud</div>
            {p.short.length > 0 && <div className="mt-1 text-xs text-muted">Mangler: {p.short.slice(0, 8).map((s) => `${s.sku} (${s.needed} bestilt, ${s.available} disponibel)`).join('; ')}{p.short.length > 8 ? '…' : ''}</div>}
          </>
        )}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button className="btn" onClick={onClose}>Annullér</button>
        <button className="btn btn-primary" disabled={!p || p.packable === 0 || create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Opretter…' : `Opret batch med ${p?.packable ?? 0} ordrer`}</button>
      </div>
    </Modal>
  );
}

export function BatchDetailPage() {
  const { id } = useParams({ from: '/app/batches/$id' });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();
  const station = useStationPrinter();
  const [error, setError] = useState<unknown>(null);
  const [tracking, setTracking] = useState('');
  const detail = useQuery({ queryKey: ['batch', id], queryFn: () => get<BatchDetailDto>(`/batches/${id}`), refetchInterval: 20_000 });
  const printers = useQuery({ queryKey: ['printers'], queryFn: () => get<PrinterDto[]>('/printing/printers') });
  const b = detail.data;
  const staging = useQuery({ queryKey: ['locations', 'staging', b?.warehouseId], queryFn: () => get<{ items: LocationDto[] }>(`/locations?warehouseId=${b!.warehouseId}&perPage=500`), enabled: !!b });
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['batch', id] }); void qc.invalidateQueries({ queryKey: ['batches'] }); void qc.invalidateQueries({ queryKey: ['shop-work'] }); void qc.invalidateQueries({ queryKey: ['orders'] }); void qc.invalidateQueries({ queryKey: ['stock'] }); void qc.invalidateQueries({ queryKey: ['fulfilments'] }); void qc.invalidateQueries({ queryKey: ['shipping-plan'] }); };
  const act = (path: string, body?: unknown) => useMutation({ mutationFn: () => post(`/batches/${id}/${path}`, body), onSuccess: invalidate, onError: setError });
  const print = useMutation({ mutationFn: () => post(`/batches/${id}/print`, { printerId: station.printerId, documents: ['overview', 'pickList', 'totals', 'giftList'] }), onSuccess: invalidate, onError: setError });
  const regenerate = act('documents');
  const startPicking = act('start-picking');
  const finishPicking = act('finish-picking');
  const pack = act('pack');
  const stage = useMutation({ mutationFn: (stagingLocationId: string | null) => post(`/batches/${id}/stage`, { stagingLocationId }), onSuccess: invalidate, onError: setError });
  const ship = useMutation({ mutationFn: () => post(`/batches/${id}/ship`, { trackingNumber: tracking || null }), onSuccess: () => { invalidate(); void navigate({ to: '/batches' }); }, onError: setError });
  const cancel = useMutation({ mutationFn: () => post(`/batches/${id}/cancel`), onSuccess: () => { invalidate(); void navigate({ to: '/batches' }); }, onError: setError });
  useEffect(() => { if (b?.trackingNumber && !tracking) setTracking(b.trackingNumber); }, [b?.trackingNumber, tracking]);
  if (!b) return <Spinner />;
  const open = !['shipped', 'cancelled'].includes(b.status);
  return (
    <div className="space-y-4">
      <PageTitle
        title={`Batch ${b.number}`}
        subtitle={<><Link to="/shops/$id" params={{ id: b.shopId }} className="text-primary hover:underline">{b.shopName}</Link> · {kindLabel[b.kind]} · {b.warehouseCode} · oprettet af {b.createdBy} {fmtTime(b.createdAt)}</>}
        actions={
          <>
            <Link to="/batches" className="btn">Tilbage</Link>
            {open && can('batches.manage') && <button className="btn" disabled={regenerate.isPending} onClick={() => regenerate.mutate()}>Gendan dokumenter</button>}
            {open && can('printing.print') && (
              <>
                <select className="input w-52" value={station.printerId} onChange={(e) => station.setPrinterId(e.target.value)}>
                  <option value="">Vælg printer</option>
                  {printers.data?.filter((x) => x.enabled && x.kind === 'pdf').map((x) => <option key={x.id} value={x.id}>{x.displayName}</option>)}
                </select>
                <button className="btn" disabled={!station.printerId || print.isPending} onClick={() => print.mutate()}>Print alle fire</button>
              </>
            )}
            {['created', 'printed'].includes(b.status) && can('floor.pick') && <button className="btn btn-primary" disabled={startPicking.isPending} onClick={() => startPicking.mutate()}>Start pluk</button>}
            {b.status === 'picking' && can('floor.pick') && <button className="btn btn-primary" disabled={finishPicking.isPending} onClick={() => confirm('Alle linjer plukkes fuldt ud fra deres lokation. Fortsæt?') && finishPicking.mutate()}>Afslut pluk</button>}
            {b.status === 'picked' && can('floor.pack') && <button className="btn btn-primary" disabled={pack.isPending} onClick={() => pack.mutate()}>Markér pakket</button>}
            {['packed', 'staged'].includes(b.status) && can('floor.pack') && <button className="btn btn-primary" disabled={ship.isPending} onClick={() => confirm(`Send ${b.number}? ${b.orderCount} ordrer sendes, og lageret trækkes.`) && ship.mutate()}>Send</button>}
            {open && can('batches.manage') && <button className="btn btn-danger" disabled={cancel.isPending} onClick={() => confirm(`Annullér ${b.number}? Ordrerne går tilbage til nye.`) && cancel.mutate()}>Annullér</button>}
          </>
        }
      />
      <ErrorBox error={error} />
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Badge tone={batchTone(b.status)}>{batchStatusLabel[b.status]}</Badge>
        <span>{b.orderCount} ordrer · {b.unitCount} stk</span>
        {b.printedAt && <span className="text-muted">printet {fmtTime(b.printedAt)}</span>}
        {b.packedBy && <span className="text-muted">pakket af {b.packedBy} {fmtTime(b.packedAt)}</span>}
        {b.shippedAt && <span className="text-muted">sendt {fmtTime(b.shippedAt)}{b.trackingNumber ? ` · ${b.trackingNumber}` : ''}</span>}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="card text-sm">
          <div className="label">Dokumenter</div>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(docLabel) as (keyof typeof b.documents)[]).map((k) => b.documents[k] ? <a key={k} className="btn" href={`/api/documents/${b.documents[k]}/download`} target="_blank" rel="noreferrer">{docLabel[k]}</a> : <span key={k} className="btn opacity-50">{docLabel[k]}</span>)}
          </div>
        </div>
        <div className="card text-sm">
          <div className="label">Leveringsadresse</div>
          {b.deliveryAddress ? <div>{[b.deliveryAddress.company, b.deliveryAddress.name, b.deliveryAddress.street1, `${b.deliveryAddress.zip ?? ''} ${b.deliveryAddress.city ?? ''}`].filter(Boolean).join(', ')}</div> : <div className="text-muted">Hver ordre sendes til sin egen adresse.</div>}
        </div>
        <div className="card text-sm">
          <div className="label">Palleplads og forsendelse</div>
          <div className="flex items-center gap-2">
            <select className="input" value={b.stagingLocationId ?? ''} disabled={!['packed', 'staged'].includes(b.status) || !can('batches.manage')} onChange={(e) => stage.mutate(e.target.value || null)}>
              <option value="">Ingen palleplads</option>
              {staging.data?.items.filter((l) => ['staging', 'pallet', 'bulk'].includes(l.type) || l.id === b.stagingLocationId).map((l) => <option key={l.id} value={l.id}>{l.code} ({l.type})</option>)}
            </select>
          </div>
          {['packed', 'staged'].includes(b.status) && <input className="input mt-2" placeholder="Track & trace uden booking (valgfrit)" value={tracking} onChange={(e) => setTracking(e.target.value)} />}
        </div>
      </div>

      {['picked', 'packed', 'staged', 'shipped'].includes(b.status) && (
        <div className="card">
          <div className="label">{b.kind === 'single_shipment' ? 'Paller' : 'Kolli'}</div>
          {b.kind === 'single_shipment' ? (
            <PackagesEditor ownerType="batch" ownerId={b.id} packages={b.packages} pallets disabled={b.status === 'shipped' || !can('floor.pack')} onSaved={invalidate} onError={setError} />
          ) : (
            <div className="text-sm text-muted">Hver ordre sendes som én pakke med indholdets vægt. Skal en ordre have flere kolli eller en anden vægt, så ret den på Pak-siden før booking.</div>
          )}
        </div>
      )}
      {['picked', 'packed', 'staged', 'shipped'].includes(b.status) && (
        <ShippingPanel ownerType="batch" ownerId={b.id} laserPrinterId={station.printerId} disabled={b.status === 'shipped'} onBooked={invalidate} onError={setError} />
      )}
      <div className="grid gap-4 xl:grid-cols-2">
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">Plukliste</h2>
          <table className="table card p-0">
            <thead><tr><th>Lokation</th><th>SKU</th><th>Vare</th><th className="text-right">På lager</th><th className="text-right">Pluk</th></tr></thead>
            <tbody>
              {b.pickList.map((p) => (
                <tr key={p.sku} className={p.onHand < p.quantity ? 'bg-warning-100/50' : ''}>
                  <td className="font-medium">{p.locationCode ?? <span className="text-danger">ingen</span>}</td>
                  <td>{p.sku}</td>
                  <td>{p.title}{p.variantTitle && <span className="text-muted"> · {p.variantTitle}</span>}</td>
                  <td className="text-right tabular-nums">{p.onHand}</td>
                  <td className="text-right tabular-nums font-semibold">{p.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">Ordrer</h2>
          <table className="table card p-0">
            <thead><tr><th>Ordre</th><th>Navn</th><th>Afdeling</th><th>Gave</th><th>Status</th></tr></thead>
            <tbody>
              {b.orders.map((o) => (
                <tr key={o.fulfilmentId}>
                  <td><Link to="/orders/$id" params={{ id: o.orderId }} className="font-medium text-primary hover:underline">{o.orderNumber}</Link></td>
                  <td>{o.customerName ?? ''}</td>
                  <td>{o.department ?? ''}</td>
                  <td className="text-xs">{o.lines.map((l) => `${l.quantity}× ${l.sku}`).join(', ')}</td>
                  <td><Link to={['picked', 'packing', 'packed', 'shipped'].includes(o.fulfilmentStatus) ? '/pack/$id' : '/pick/$id'} params={{ id: o.fulfilmentId }} className="text-xs text-primary hover:underline">{o.fulfilmentNumber}</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
